import { useState, useEffect } from 'react';
import { getBlogPosts, getBlogPost, getFeaturedPosts, getPostsByCategory, getPostsByTag, getCategories, getTags, getAuthor, getRelatedPosts } from '../lib/sanity';
import { BlogPost, BlogPostPreview, Category, Tag, Author } from '../types/blog';

export const useBlogPosts = () => {
  const [posts, setPosts] = useState<BlogPostPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const data = await getBlogPosts();
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return { posts, loading, error };
};

export const useBlogPost = (slug: string) => {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const data = await getBlogPost(slug);
        setPost(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch blog post');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  return { post, loading, error };
};

export const useFeaturedPosts = (limit: number = 3) => {
  const [posts, setPosts] = useState<BlogPostPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedPosts = async () => {
      try {
        setLoading(true);
        const data = await getFeaturedPosts(limit);
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch featured posts');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedPosts();
  }, [limit]);

  return { posts, loading, error };
};

export const usePostsByCategory = (category: string) => {
  const [posts, setPosts] = useState<BlogPostPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPostsByCategory = async () => {
      try {
        setLoading(true);
        const data = await getPostsByCategory(category);
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch posts by category');
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      fetchPostsByCategory();
    }
  }, [category]);

  return { posts, loading, error };
};

export const usePostsByTag = (tag: string) => {
  const [posts, setPosts] = useState<BlogPostPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPostsByTag = async () => {
      try {
        setLoading(true);
        const data = await getPostsByTag(tag);
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch posts by tag');
      } finally {
        setLoading(false);
      }
    };

    if (tag) {
      fetchPostsByTag();
    }
  }, [tag]);

  return { posts, loading, error };
};

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await getCategories();
        setCategories(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch categories');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return { categories, loading, error };
};

export const useTags = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoading(true);
        const data = await getTags();
        setTags(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tags');
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  return { tags, loading, error };
};

export const useAuthor = (slug: string) => {
  const [author, setAuthor] = useState<Author | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAuthor = async () => {
      try {
        setLoading(true);
        const data = await getAuthor(slug);
        setAuthor(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch author');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchAuthor();
    }
  }, [slug]);

  return { author, loading, error };
};

export const useRelatedPosts = (currentPostId: string, categorySlugs: string[], tagSlugs: string[], limit: number = 3) => {
  const [posts, setPosts] = useState<BlogPostPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        setLoading(true);
        const data = await getRelatedPosts(currentPostId, categorySlugs, tagSlugs, limit);
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch related posts');
      } finally {
        setLoading(false);
      }
    };

    if (currentPostId && (categorySlugs.length > 0 || tagSlugs.length > 0)) {
      fetchRelatedPosts();
    }
  }, [currentPostId, categorySlugs, tagSlugs, limit]);

  return { posts, loading, error };
}; 